import {
	newTripRequest,
	updateTripRequest,
	updateTripOrdersRequest,
	openTmsR10Request,
	getCurrentClerkRequest,
	sendLegRequest,
	broadcastLegRequest,
	getTripByIdRequest,
	scheduleSuborderOnTripRequest,
	unscheduleSuborderOnTripRequest,
	getTripChangelogRequest
} from '../requests';

import {
	throwTranslatableError,
	sumValues,
	baseUrl,
	newUUID,
	createDateObject,
	storageDateFormat,
} from '@/core';

import { leg as legStructure } from '@/dataStructures';
import { moveOrderToDifferentTrip } from './actions';

import * as validators from '@/modules/newTrip/validation';

import rdbClient from 'rdb-client';
import moment from 'moment';

/* Helpers */
const getDateObject = (currentDateObj, { dateValue, timeValue, hasTime }) => createDateObject({
	date: dateValue ? dateValue : currentDateObj.date,
	time: timeValue ? moment(timeValue, 'LT').format('HH:mm') : moment(currentDateObj.date, storageDateFormat).format('HH:mm'),
	hasTime: (hasTime === null) ? currentDateObj.hasTime : hasTime,
}, Boolean(dateValue));
/* ------- */

/* Setters */
function setStatus({ state, commit, dispatch }, status) {
	commit('setValue', { path: `status`, value: status });
	if (state.trip.legs.length === 1) {
		dispatch('setLegStatus', { legNumber: 0, status });
	}
	if (status < 10) { // Reset all errors if setting draft/canceled status
		commit('setError', { path: '', value: {} });
	}
}

function setType({ state, commit, dispatch }, value) {
	commit('setValue', { path: `type`, value });

	if ((value.internalCode || '').toUpperCase() === 'L') {
		if (!state.trip.toAddress.id) {
			dispatch('setToAddress', state.trip.fromAddress);
		} else if (!state.trip.fromAddress.id) {
			dispatch('setFromAddress', state.trip.toAddress);
		}
	}
	dispatch('validateFromAndToAddresses');
}

function setClerk({ commit }, value) {
	commit('setValue', { path: `clerk`, value });
}

function setFromAddress({ state, commit, dispatch }, value) {
	commit('setValue', { path: `fromAddress`, value });
	if (state.trip.legs.length === 1) {
		dispatch('setLegFromAddress', { legNumber: 0, ...value });
	}
	if ((state.trip.type.internalCode || '').toUpperCase() === 'L'
		&& value.id
		&& !state.trip.toAddress.id) {
		dispatch('setToAddress', value);
		// We don't validate here, because we know the validation
		// will be run by the setToAddress action.
	} else {
		dispatch('validateFromAndToAddresses');
	}
}

function setToAddress({ state, commit, dispatch }, value) {
	commit('setValue', { path: `toAddress`, value });
	if (state.trip.legs.length === 1) {
		dispatch('setLegToAddress', { legNumber: 0, ...value });
	}
	if ((state.trip.type.internalCode || '').toUpperCase() === 'L'
		&& value.id
		&& !state.trip.fromAddress.id) {
		dispatch('setFromAddress', value);
	} else {
		dispatch('validateFromAndToAddresses');
	}
}

function setEtd({ state, commit, dispatch }, value) {
	let dateObject = getDateObject(state.trip.etd, value);
	commit('setValue', { path: `etd`, value: dateObject });
	if (state.trip.legs.length === 1) {
		dispatch('setLegEtd', { legNumber: 0, ...value });
	}
	if (dateObject.epoch > state.trip.eta.epoch) {
		dispatch('setEta', { ...value, hasTime: false });
	}
	dispatch('validateEtdAndEta');
}

function setEta({ state, commit, dispatch }, value) {
	let dateObject = getDateObject(state.trip.eta, value);
	commit('setValue', { path: `eta`, value: dateObject });
	if (state.trip.legs.length === 1) {
		dispatch('setLegEta', { legNumber: 0, ...value });
	}
	dispatch('validateEtdAndEta');
}

function setCargoCarrier({ commit, dispatch }, cargoCarrier) {
	if (!Object.keys(cargoCarrier).length) {
		commit('setValue', { path: 'cargoCarrier', value: undefined });
		return;
	}
	if (cargoCarrier.cargoCarrierType && cargoCarrier.cargoCarrierType.code.includes('L')) { // Cargo carrier can carry load.
		commit('setValue', { path: 'cargoCarrier', value: cargoCarrier });
	} else {
		throw new Error('Invalid Cargo Carrier! This cargo carrier cannot carry any load.');
	}
	if (cargoCarrier.vendor) dispatch('setVendor', cargoCarrier.vendor);
	if (cargoCarrier.cargoCarrierType && cargoCarrier.cargoCarrierType.code === 'LB') { // Cargo carrier can transport itself.
		dispatch('setLegCargoCarrier', { legNumber: 0, ...cargoCarrier });
	}
}

function setVendor({ commit, dispatch }, value) {
	commit('setValue', { path: `vendor`, value });
	dispatch('validateVendor');
}
/* ------- */

/* Validators */
const createValidatorAction = validator => (...params) => {
	let [{ state }] = params;
	if (!state.errors.isInvalid) return; // Only start validating single fields once errors are shown.
	if (state.trip.status < 10) return; // Trip status is "draft" or "cancelled", so anything goes.
	validator(...params);
};

const validateFromAndToAddresses = createValidatorAction(({ state, commit }) => {
	let allowEqualAddresses = Boolean(state.trip.type.internalCode === 'L');
	let errors = validators.fromAndToAddresses(state.trip, allowEqualAddresses);
	commit('setError', { path: 'fromAddress', value: errors.fromAddress });
	commit('setError', { path: 'toAddress', value: errors.toAddress });
});

const validateEtdAndEta = createValidatorAction(({ state, commit }) => {
	let errors = validators.etdAndEta(state.trip);
	commit('setError', { path: 'eta', value: errors.eta });
	commit('setError', { path: 'etd', value: errors.etd });
});

const validateVendor = createValidatorAction(({ state, commit }) => {
	commit('setError', { path: 'vendor', value: validators.vendor(state.trip.vendor) });
});

function validateTrip({ state, commit }, { validateDrivers=false }={}) {
	if (state.trip.status < 10) {
		return; // Trips status is "draft" or "cancelled", so anything goes!
	}
	let errors = validators.trip(state.trip, { validateDrivers });
	commit('setError', { path: '', value: errors });
}
/* ------- */

/* ---- Legs ---- */
function setLegStatus({ commit }, { legNumber, status }) {
	commit('setValue', { path: `legs.${legNumber}.status`, value: status });
}

function setLegFromAddress({ commit, dispatch }, { legNumber, ...value }) {
	commit('setValue', { path: `legs.${legNumber}.fromAddress`, value });
	dispatch('validateLegFromAndToAddresses', legNumber);
}

function setLegToAddress({ commit, dispatch }, { legNumber, ...value }) {
	commit('setValue', { path: `legs.${legNumber}.toAddress`, value });
	dispatch('validateLegFromAndToAddresses', legNumber);
}

function setLegEtd({ state, commit, dispatch }, { legNumber, ...value }) {
	let dateObject = getDateObject(state.trip.legs[legNumber].etd, value);
	commit('setValue', { path: `legs.${legNumber}.etd`, value: dateObject });

	if (dateObject.epoch > state.trip.legs[legNumber].eta.epoch) {
		dispatch('setLegEta', { ...value, hasTime: false, legNumber });
	}
	dispatch('validateLegEtdAndEta', legNumber);
}

function setLegEta({ state, commit, dispatch }, { legNumber, ...value }) {
	let dateObject = getDateObject(state.trip.legs[legNumber].eta, value);
	commit('setValue', { path: `legs.${legNumber}.eta`, value: dateObject });
	dispatch('validateLegEtdAndEta', legNumber);
}

function setLegCargoCarrier({ commit, dispatch }, { legNumber, ...value }) {
	if (!value.id) {
		commit('setValue', {  path: `legs.${legNumber}.cargoCarrier`, value: undefined });
		return;
	}

	commit('setValue', {  path: `legs.${legNumber}.cargoCarrier`, value });
	if (value.vendor) dispatch('setLegVendor', { legNumber, ...value.vendor });
	if (value.driver) dispatch('setLegDriver', { legNumber, fromCargoCarrier: true, ...value.driver });
}

function setLegVendor({ commit }, { legNumber, ...value }) {
	commit('setValue', { path: `legs.${legNumber}.vendor`, value });
}

function setLegNeedAccept({ commit }, { legNumber, needAccept }) {
	commit('setValue', { path: `legs.${legNumber}.needAccept`, needAccept });

}

function setLegDriver({ state, commit, dispatch }, { legNumber, fromCargoCarrier, ...value }) {
	if (fromCargoCarrier) {
		if (state.trip.legs[legNumber].driverIsSetManually) return;
	} else {
		commit('setValue', { path: `legs.${legNumber}.driverIsSetManually`, value: true });
	}
	commit('setValue', { path: `legs.${legNumber}.driver`, value });
	dispatch('validateLegDrivers', legNumber);
}

function setLegBroadcastDrivers({ commit, dispatch }, { legNumber, broadcastDrivers }) {
	commit('setValue', { path: `legs.${legNumber}.broadcastDrivers`, value: broadcastDrivers });
	dispatch('validateLegDrivers', legNumber);
}
/* -------------- */

/* Leg validation */
const validateLegFromAndToAddresses = createValidatorAction(({ state, commit }, legNumber) => {
	let allowEqualAddresses = true; // We don't want leg-validation to be this complicated.
	let errors = validators.fromAndToAddresses(state.trip.legs[legNumber], allowEqualAddresses);
	commit('setError', { path: `legs.${legNumber}.fromAddress`, value: errors.fromAddress });
	commit('setError', { path: `legs.${legNumber}.toAddress`, value: errors.toAddress });
});

const validateLegEtdAndEta = createValidatorAction(({ state, commit }, legNumber) => {
	let errors = validators.etdAndEta(state.trip.legs[legNumber]);
	commit('setError', { path: `legs.${legNumber}.eta`, value: errors.eta });
	commit('setError', { path: `legs.${legNumber}.etd`, value: errors.etd });
});

const validateLegDrivers = createValidatorAction(({ state, commit }, legNumber) => {
	if (!state.errors.legs[legNumber].driversIsInvalid) return;
	let { driver, broadcastDrivers, isInvalid } = validators.drivers(state.trip.legs[legNumber]);
	commit('setError', { path: `legs.${legNumber}.driver`, value: driver });
	commit('setError', { path: `legs.${legNumber}.broadcastDrivers`, value: broadcastDrivers });
	commit('setError', { path: `legs.${legNumber}.driversIsInvalid`, value: isInvalid });
});
/* -------------- */

function setDeviations({ commit }, deviations) {
	commit('setValue', { path: 'deviations', value: deviations });
}

function addViaLocation({ commit, state }) {
	let body = {
		id: newUUID(),
		viaNumber: state.trip.viaLocations.length,
		address: {},
		dateTimeObject: (state.trip.etd.date.slice(0, -4) === state.trip.eta.date.slice(0, -4)) ? state.trip.etd : undefined,
	};
	commit('updateViaLocation', { update: body, index: state.trip.viaLocations.length });
}

function setViaLocationDate({ commit, state }, { index, ...value }) {
	let dateObject = getDateObject(state.trip.viaLocations[index].dateTimeObject, value);
	commit('updateViaLocation', { index, update: { dateTimeObject: dateObject } });
}

function addLeg({ commit, state }) {
	let legNumber = state.trip.legs.length;
	let defaultLeg = legStructure();
	let leg = {
		...defaultLeg,
		tripId: state.trip.id,
		legNumber,
		status: state.trip.status,
		etd: createDateObject({ date: state.trip.etd.date, hasTime: false }, false),
		eta: createDateObject({ date: state.trip.eta.date, hasTime: false }, false),
		id: defaultLeg.id
	};
	commit('set', state => {
		state.trip.legs.push(leg);
	});
}

async function saveNewTrip({ state, commit, dispatch }, { validateDrivers, closeDialog }) {
	dispatch('validateTrip', { validateDrivers });
	if (state.errors.isInvalid) {
		throw new Error('validationErrors');
	}
	try {
		let response = await newTripRequest(state.trip);
		if (closeDialog) commit('cleanState', state.trip.clerk);
		else commit('setEditTrip', response);
	} catch (e) {
		throwTranslatableError(e, 'failedToSave');
	}
}

async function updateTrip({ state, commit, dispatch }, { validateDrivers, closeDialog }) {
	dispatch('validateTrip', { validateDrivers });
	if (state.errors.isInvalid) {
		throw new Error('validationErrors');
	}

	let tripPatches = rdbClient.createPatch(state.tripClone, state.trip);

	try {
		let response = await updateTripRequest(state.trip.id, tripPatches);
		if (!closeDialog) commit('setEditTrip', response);
		state.dirty = false;
	} catch (e) {
		throwTranslatableError(e, 'failedToSave');
	}
	dispatch('fetchTripChangelog');
}

async function setEditTrip({ commit, dispatch }, id) {
	commit('setTripLoaded', false);
	try {
		let response = await getTripByIdRequest(id);
		commit('setEditTrip', response);
		dispatch('fetchTripChangelog');
	} catch (e) {
		console.error(e);
		throwTranslatableError(e, 'failedToFetch');
	}
	commit('setTripLoaded', true);
}

async function openTmsR10({}, trip) {
	await openTmsR10Request(trip.id);
}

async function sendLegToConfirm({ commit, state }, legId) {
	if (!state.trip.id) throw new Error('needToSaveTrip');
	try {
		let response = await sendLegRequest({ legId, tripId: state.trip.id });
		commit('setEditTrip', response);
	} catch (error) {
		throwTranslatableError(error, 'failedToSendLeg');
	}
}

async function broadcastToDrivers({ commit, state }, legId) {
	if (!state.trip.id) throw new Error('needToSaveTrip');
	try {
		let response = await broadcastLegRequest({ legId, tripId: state.trip.id });
		commit('setEditTrip', response);
	} catch (error) {
		throwTranslatableError(error, 'failedToBroadcastLeg');
	}
}

async function cleanState({ commit }) {
	let currentClerk = await getCurrentClerkRequest();
	commit('cleanState', currentClerk);
}

async function removeOrdersFromTrip({ state, commit, dispatch }, { orders, merge }) {
	if (!orders || !state.trip || !state.trip.orders) return;
	let { trip } = state;

	let subOrderUpdates = [];
	orders.forEach(order => {
		subOrderUpdates.push({
			removedOrderId: order.id,
			merge
		});
	});

	// Remove orders
	let cleanOrderList = trip.orders.filter(order => orders.findIndex( x => x.id === order.id) === -1);
	trip.volume = sumValues(cleanOrderList, 'volume');
	trip.grossWeight = sumValues(cleanOrderList, 'grossWeight');
	trip.loadingMeters = sumValues(cleanOrderList, 'loadingMeters');

	try {
		let patch = rdbClient.createPatch(state.tripClone, trip);
		let response = await updateTripOrdersRequest(trip.id, { tripPatches: patch, subOrderUpdates });
		commit('setEditTrip', response);
		dispatch('fetchTripChangelog');
	} catch (err) {
		console.log(err);
		throw new Error('failedToRemoveOrderTrip');
	}
}
function getWaybill(_, orderIds) {
	let base = baseUrl();

	if (Array.isArray(orderIds) && orderIds.length > 1) {
		let form = document.createElement('form');
		form.target = '_blank';
		form.method = 'post';
		form.action = `${base}/documents/wayBill/merged`;
		form.style.display = 'none';

		let input = document.createElement('input');
		input.type = 'text';
		input.required = 'required';
		input.name = 'ids';
		input.value = orderIds;
		form.appendChild(input);

		document.body.appendChild(form);
		form.submit();
		document.body.removeChild(form);
	} else {
		let url = `${base}/documents/wayBill/${orderIds}`;
		window.open(url);
	}
}

function getPackageLabel(_, orderIds) {
	let base = baseUrl();
	let locale = localStorage.getItem('locale');

	if (Array.isArray(orderIds) && orderIds.length > 1) {
		let form = document.createElement('form');
		form.target = '_blank';
		form.method = 'post';
		form.action = `${base}/documents/packageLabel/${locale}/merged`;
		form.style.display = 'none';

		let input = document.createElement('input');
		input.type = 'text';
		input.required = 'required';
		input.name = 'ids';
		input.value = orderIds;
		form.appendChild(input);

		document.body.appendChild(form);
		form.submit();
		document.body.removeChild(form);
	} else {
		let url = `${base}/documents/packageLabel/${locale}/${orderIds}`;
		window.open(url);
	}
}

async function scheduleSubOrderForEdi(_, obj) {
	//30 external transporterOrder
	if (obj.type === 30)
		return;
	else
		await scheduleSuborderOnTripRequest(obj.id, obj.subOrderId);
}

async function unscheduleSubOrderForEdi({  commit }, obj) {
	let response;
	if (obj.type === 30)
		return;
	else
		response = await unscheduleSuborderOnTripRequest(obj.id, obj.subOrderId);
	commit('setEditTrip', response);
}


async function fetchTripChangelog({ commit, state }) {
	try {
		let changelog = await getTripChangelogRequest(state.trip.id);
		commit('setTripChangelog', changelog);
	} catch (e) {
		console.error(e);
		throwTranslatableError(e, 'failedToFetch');
	}
}

async function updateLoadingSequence({ commit, state }, mapOrderIdToNewLoadingSeqNo) {

	let tripWithNewOrderSequence = { ...state.trip };

	tripWithNewOrderSequence.orders
		= state.trip.orders.map(
			order => ({
				...order,
				loadingSeqNo: mapOrderIdToNewLoadingSeqNo[order.id]
			})
		);

	let tripPatches = rdbClient.createPatch(state.trip, tripWithNewOrderSequence);

	try {
		let response = await updateTripOrdersRequest(state.trip.id, { tripPatches });

		commit('setOrders', response.orders);
	} catch (err) {
		throw new Error('failedToChangeLoadingSequence');
	}
}

export default {
	setStatus,
	setType,
	setClerk,
	setFromAddress,
	setToAddress,
	setEtd,
	setEta,
	setCargoCarrier,
	setVendor,
	validateFromAndToAddresses,
	validateEtdAndEta,
	validateVendor,
	setLegStatus,
	setLegFromAddress,
	setLegToAddress,
	setLegEtd,
	setLegEta,
	setLegCargoCarrier,
	setLegVendor,
	setLegNeedAccept,
	setLegDriver,
	validateLegFromAndToAddresses,
	validateLegEtdAndEta,
	validateLegDrivers,
	setLegBroadcastDrivers,
	validateTrip,
	setViaLocationDate,
	setDeviations,
	saveNewTrip,
	updateTrip,
	setEditTrip,
	addLeg,
	cleanState,
	openTmsR10,
	sendLegToConfirm,
	broadcastToDrivers,
	addViaLocation,
	removeOrdersFromTrip,
	getWaybill,
	getPackageLabel,
	scheduleSubOrderForEdi,
	unscheduleSubOrderForEdi,
	moveOrderToDifferentTrip,
	fetchTripChangelog,
	updateLoadingSequence,
};
